import React, { useEffect, useState } from 'react';
import { queryCache } from 'react-query';
import { QKeys } from 'src/api/types';
import { GTCInlineWrapper } from 'src/components/GenericTable';
import { useUpsertControlMutation } from 'src/graphql/_generated';
import { useAuth } from 'src/state/auth';
import { getCurrencySymbol } from 'src/utils/misc';
import { NumericFormat } from 'react-number-format';
import styled from 'styled-components';

const StyledInput = styled.input`
  width: 100%;
  height: 100%;
  border: none;
  outline: none;
  background: transparent;
  font: inherit;
  color: ${({ theme }) => theme.colors.prussianBlue};
  padding: 0 14px;
  user-select: text;
`;

interface AnnualCostCellProps {
  controlId: string;
  value: number | null | undefined;
}

export const AnnualCostCell: React.FC<AnnualCostCellProps> = ({ controlId, value }) => {
  const [upsertControl, { loading }] = useUpsertControlMutation();
  const { workspaceId } = useAuth();
  const [draft, setDraft] = useState<number | undefined>(value ?? undefined);

  useEffect(() => {
    setDraft(value ?? undefined);
  }, [value]);

  const save = () => {
    const newValue = draft ?? null;
    const oldValue = value ?? null;
    if (newValue === oldValue) return;
    upsertControl({
      variables: {
        controlData: { id: controlId, annualCost: newValue },
        workspaceId,
      },
      update: (cache) => {
        cache.modify({
          id: cache.identify({ __typename: 'Control', id: controlId }),
          fields: { annualCost: () => newValue },
        });
      },
      onCompleted: () => queryCache.invalidateQueries([QKeys.Audit]),
    });
  };

  return (
    <GTCInlineWrapper>
      <NumericFormat
        customInput={StyledInput}
        thousandSeparator=","
        prefix={getCurrencySymbol()}
        value={draft ?? ''}
        onValueChange={({ floatValue }) => setDraft(floatValue)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
        }}
        disabled={loading}
        placeholder="-"
      />
    </GTCInlineWrapper>
  );
};
